// Import Types
import type {
    CMSBanner,
    DataWithPaging,
    DownloadFileResponse,
    ProcessTemplateWithAiResponse,
    ShorterAndTrackerSettings,
    TemplateById,
    TemplateLinkManagement,
    TemplateListItem,
    TemplateModel,
    WhatsappTemplateApiResponse,
} from 'types';
// Import Helpers
import { generateCleanUrlParams } from 'services/Helpers';
// Import Configs
import {
    CRMRequestService,
    CRMGalleryRequestService,
    getCancelableRequest,
    postCancelableRequest,
    deleteCancelableRequest,
} from './http.config';
// Import Constants
import { TemplateTypesLabels } from 'constants/common';

const TemplateHttpService = {
    getTemplate: () => {
        return getCancelableRequest<TemplateById>({
            requestService: CRMRequestService,
            defaultParams: { isClone: false },
            endpointPath: ({ templateId, pointInTime, isClone }) =>
                `/Template/Get${generateCleanUrlParams({ id: templateId, pointInTime, isClone })}`,
        });
    },
    getCampaignsByTemplate: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ templateId, pointInTime }) =>
                `/Template/CampaignsByTemplateId${generateCleanUrlParams({ templateId, pointInTime })}`,
        });
    },
    getTemplateList: () => {
        return postCancelableRequest<DataWithPaging<TemplateListItem>>({
            requestService: CRMRequestService,
            endpointPath: () => '/Template/List',
        });
    },
    deleteTemplate: () => {
        return deleteCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ templateId }) => `/Template/Delete${generateCleanUrlParams({ id: templateId })}`,
        });
    },
    deleteTemplates: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Template/Delete/`,
        });
    },
    archiveTemplate: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ templateId }) => `/Template/Archive${generateCleanUrlParams({ id: templateId })}`,
        });
    },
    archiveTemplates: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Template/Archive`,
        });
    },
    unarchiveTemplate: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ templateId }) => `/Template/Unarchive${generateCleanUrlParams({ id: templateId })}`,
        });
    },
    createTemplate: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Template/CreateMulti`,
        });
    },
    unarchiveTemplates: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Template/unArchive`,
        });
    },
    editTemplate: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Template/UpdateMulti`,
        });
    },
    getTemplateModels: () => {
        return getCancelableRequest<TemplateModel[]>({
            requestService: CRMRequestService,
            defaultParams: { eventId: 0, channel: TemplateTypesLabels.Email },
            endpointPath: ({ eventId, channel }) => `/Template/GetTemplateModels?channel=${channel}&eventId=${eventId}`,
        });
    },
    getTemplateModelsById: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ templateId }) => `/Template/GetTemplateModelsById?templateId=${templateId}`,
        });
    },
    testSendTemplate: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Template/Send',
        });
    },
    getTemplateById: () => {
        return getCancelableRequest<TemplateById[]>({
            requestService: CRMRequestService,
            defaultParams: { isClone: false },
            endpointPath: ({ templateId, isClone }) =>
                `/Template/GetMulti${generateCleanUrlParams({ id: templateId, isClone })}`,
        });
    },
    getSMSPartsCount: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ message }) => `/Template/GetSmsPartsCount?message=${message}`,
        });
    },
    getMaxSMSPartsCount: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/PartnerSettings/GetMaxSmsPartsCount',
        });
    },
    getAssets: () => {
        return getCancelableRequest<DownloadFileResponse[]>({
            requestService: CRMGalleryRequestService,
            endpointPath: () => `/List/Image`,
        });
    },
    addAsset: () => {
        return postCancelableRequest<DownloadFileResponse[]>({
            requestService: CRMGalleryRequestService,
            endpointPath: () => 'Save/Image',
        });
    },
    getShorterAndTrackerSettings: () => {
        return getCancelableRequest<ShorterAndTrackerSettings[]>({
            requestService: CRMRequestService,
            endpointPath: ({ deliveryMethod }) =>
                `/Template/GetShorterAndTrackerSettings?deliveryMethod=${deliveryMethod}`,
        });
    },
    getLinkManagement: () => {
        return getCancelableRequest<TemplateLinkManagement>({
            requestService: CRMRequestService,
            endpointPath: () => `/Template/GetLinkManagement`,
        });
    },
    updateLinkManagement: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Template/UpdateLinkManagement`,
        });
    },
    updateExcludedTrackingLinks: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Template/UpdateIgnoredTrackLinks`,
        });
    },
    getUrlRegex: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Template/GetUrlRegex`,
        });
    },
    getWhatsAppTemplates: () => {
        return getCancelableRequest<WhatsappTemplateApiResponse[]>({
            requestService: CRMRequestService,
            endpointPath: ({ templateName }) =>
                `/Template/GetWhatsAppTemplates${generateCleanUrlParams({ templateName })}`,
        });
    },
    getCMSBanners: () => {
        return getCancelableRequest<CMSBanner[]>({
            requestService: CRMRequestService,
            endpointPath: ({ name, id }) => `/Template/GetCMSBanners${generateCleanUrlParams({ name, id })}`,
        });
    },
    processTemplateWithAi: () => {
        return postCancelableRequest<ProcessTemplateWithAiResponse>({
            requestService: CRMRequestService,
            endpointPath: () => `/Template/ProcessTemplateWithAi`,
        });
    },
};

export default TemplateHttpService;
